// src/components/Pentests/runs/TechFingerprintView.js
//
// Tech Fingerprint results with NVD CVE enrichment (Phase B). Each row can
// be enriched individually or in bulk; results are cached server-side
// (settings.cve_cache_ttl) so re-opens are instant.

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Cpu, ExternalLink, Search, Sparkles } from 'lucide-react';
import { cveEnrichAPI } from '../../../services/api';

const SEVERITY_STYLE = {
  CRITICAL: 'bg-red-100 text-red-700 border-red-300',
  HIGH: 'bg-orange-100 text-orange-700 border-orange-300',
  MEDIUM: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  LOW: 'bg-blue-100 text-blue-700 border-blue-300',
  UNKNOWN: 'bg-slate-100 text-slate-700 border-slate-300',
};

const techKey = (name, version) => `${name}::${version || ''}`;

const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3, info: 4, unknown: 5 };

// Tier ordering for the row sort. Lower number floats to the top.
//   0..5  rows that have CVEs, ranked by their max severity
//   10    rows with versions whose enrichment is in flight or never ran
//   20    rows that were enriched and came back clean (no CVEs)
//   30    rows without a version (no enrichable signal)
function rowTier(row, entry) {
  if (!row.version) return 30;
  if (!entry) return 10;
  const cves = entry.cves || [];
  if (cves.length === 0) return 20;
  let best = 5;
  for (const c of cves) {
    const s = (c.severity || 'unknown').toLowerCase();
    const r = SEV_RANK[s];
    if (r != null && r < best) best = r;
  }
  return best;
}

export default function TechFingerprintView({ results }) {
  const [filter, setFilter] = useState('');
  const [cveByKey, setCveByKey] = useState(new Map());
  const [loadingKeys, setLoadingKeys] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());
  const [available, setAvailable] = useState(null); // null = unknown
  const [error, setError] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const rows = useMemo(() => {
    return results.map((r) => {
      const d = r.data || {};
      const versionFromArray = Array.isArray(d.versions) && d.versions.length > 0 ? d.versions[0] : '';
      return {
        id: r.id,
        name: d.technology || r.value,
        version: d.version || versionFromArray || '',
        categories: d.categories || [],
        url: d.url || d.target || '',
      };
    });
  }, [results]);

  // Seed cveByKey from server-stored data.cves so auto-enrichment results
  // (written by the enrich_scan_cves Celery task into ScanResult.data) show
  // up without requiring a manual Enrich click. Empty arrays are seeded too
  // so "checked, no CVEs" rows render as a green pill instead of an Enrich
  // button.
  useEffect(() => {
    setCveByKey((prev) => {
      const next = new Map(prev);
      let changed = false;
      for (const r of results) {
        const d = r.data || {};
        if (!Array.isArray(d.cves)) continue;
        const versionFromArray = Array.isArray(d.versions) && d.versions.length > 0 ? d.versions[0] : '';
        const name = d.technology || r.value;
        const version = d.version || versionFromArray || '';
        if (!version) continue;
        const k = techKey(name, version);
        if (!next.has(k)) {
          next.set(k, { technology: name, version, cves: d.cves, count: d.cves.length });
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [results]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = !q
      ? rows
      : rows.filter((r) =>
          [r.name, r.version, r.url, ...(r.categories || [])].some((s) =>
            String(s).toLowerCase().includes(q)
          )
        );
    return [...base].sort((a, b) => {
      const ta = rowTier(a, cveByKey.get(techKey(a.name, a.version)));
      const tb = rowTier(b, cveByKey.get(techKey(b.name, b.version)));
      if (ta !== tb) return ta - tb;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [rows, filter, cveByKey]);

  const toggleExpanded = (k) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const enrich = async (items) => {
    if (items.length === 0) return;
    const keys = items.map((it) => techKey(it.technology, it.version));
    setLoadingKeys((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => next.add(k));
      return next;
    });
    setError(null);
    try {
      const resp = await cveEnrichAPI.enrich(items);
      setAvailable(resp.available);
      setCveByKey((prev) => {
        const next = new Map(prev);
        for (const entry of resp.results || []) {
          const k = techKey(entry.technology, entry.version);
          next.set(k, entry);
        }
        return next;
      });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoadingKeys((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next;
      });
    }
  };

  const enrichOne = (r) => enrich([{ technology: r.name, version: r.version }]);

  const enrichAll = async () => {
    setBulkRunning(true);
    try {
      // Only enrich rows that have a version and aren't already loaded.
      const queue = rows.filter(
        (r) => r.version && !cveByKey.has(techKey(r.name, r.version)),
      );
      // Batch in groups of 25 to fit within the endpoint's 50-item cap and
      // give incremental UI updates.
      for (let i = 0; i < queue.length; i += 25) {
        const slice = queue.slice(i, i + 25);
        await enrich(slice.map((r) => ({ technology: r.name, version: r.version })));
      }
    } finally {
      setBulkRunning(false);
    }
  };

  if (results.length === 0) {
    return (
      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
        <Cpu className="w-6 h-6 mx-auto mb-2 opacity-60" />
        No technologies fingerprinted yet.
      </div>
    );
  }

  const enrichedCount = rows.filter((r) => cveByKey.has(techKey(r.name, r.version))).length;

  return (
    <div className="space-y-3">
      {available === false && (
        <div className="rounded-xl border border-amber-300 bg-amber-100 p-3 flex items-start gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-amber-700/90">
            <span className="font-semibold text-amber-700">No NVD API key set.</span>{' '}
            Lookups still work but are heavily rate-limited (5 req / 30s) and may return empty arrays.
            Set <code className="font-mono text-xs px-1 rounded bg-amber-100">NVD_API_KEY</code> on the server for normal throughput.
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter technologies..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-cyber-100 border border-cyber-300/60 rounded-lg text-slate-900 placeholder-slate-500 outline-none focus:border-shinobi-green/70"
          />
        </div>
        <button
          type="button"
          onClick={enrichAll}
          disabled={bulkRunning}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border border-wasabi-300 bg-wasabi-100 text-wasabi-700 hover:bg-wasabi-100 disabled:opacity-50"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {bulkRunning ? `Enriching (${enrichedCount}/${rows.length})...` : `Enrich all (${enrichedCount}/${rows.length})`}
        </button>
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {filtered.length === rows.length ? `${rows.length} techs` : `${filtered.length} of ${rows.length}`}
        </span>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-100 p-2 text-sm text-red-700">{error}</div>
      )}

      <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
        <table className="w-full text-sm text-left">
          <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
            <tr>
              <th className="px-3 py-3 w-6"></th>
              <th className="px-4 py-3">Technology</th>
              <th className="px-4 py-3">Version</th>
              <th className="px-4 py-3">Categories</th>
              <th className="px-4 py-3">URL</th>
              <th className="px-4 py-3">CVEs</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const k = techKey(r.name, r.version);
              const entry = cveByKey.get(k);
              const isLoading = loadingKeys.has(k);
              const isOpen = expanded.has(k);
              const cves = entry?.cves || [];
              const canEnrich = !!r.version;
              return (
                <FragmentRow
                  key={r.id}
                  row={r}
                  k={k}
                  entry={entry}
                  isOpen={isOpen}
                  isLoading={isLoading}
                  canEnrich={canEnrich}
                  cves={cves}
                  onToggle={() => toggleExpanded(k)}
                  onEnrich={() => enrichOne(r)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FragmentRow({ row, k, entry, isOpen, isLoading, canEnrich, cves, onToggle, onEnrich }) {
  return (
    <>
      <tr
        onClick={() => entry && cves.length > 0 && onToggle()}
        className={`border-t border-cyber-300/30 hover:bg-cyber-200/40 ${entry && cves.length > 0 ? 'cursor-pointer' : ''}`}
      >
        <td className="px-3 py-2 text-slate-500">
          {entry && cves.length > 0
            ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)
            : null}
        </td>
        <td className="px-4 py-2 font-mono text-shinobi-green break-all">{row.name}</td>
        <td className="px-4 py-2">
          {row.version ? (
            <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-mono bg-cyan-100 text-cyan-700 border border-cyan-300">
              {row.version}
            </span>
          ) : (
            <span className="text-slate-500 text-xs">-</span>
          )}
        </td>
        <td className="px-4 py-2">
          {row.categories.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {row.categories.slice(0, 4).map((c) => (
                <span key={c} className="px-1.5 py-0.5 rounded text-[10px] bg-cyber-200 text-slate-700">{c}</span>
              ))}
            </div>
          ) : <span className="text-slate-500">-</span>}
        </td>
        <td className="px-4 py-2 font-mono text-xs text-slate-500 break-all max-w-md">{row.url || '-'}</td>
        <td className="px-4 py-2">
          <CveCell
            entry={entry}
            isLoading={isLoading}
            canEnrich={canEnrich}
            onEnrich={onEnrich}
          />
        </td>
      </tr>
      {isOpen && entry && cves.length > 0 && (
        <tr className="bg-cyber-100/40">
          <td colSpan={6} className="px-6 py-3">
            <div className="overflow-hidden rounded-lg border border-cyber-300/40">
              <table className="w-full text-xs text-left">
                <thead className="bg-cyber-200/60 text-[10px] uppercase tracking-wider text-slate-700">
                  <tr>
                    <th className="px-3 py-2">CVE</th>
                    <th className="px-3 py-2">CVSS</th>
                    <th className="px-3 py-2">Severity</th>
                    <th className="px-3 py-2">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {cves.map((c) => (
                    <tr key={c.id} className="border-t border-cyber-300/30">
                      <td className="px-3 py-1.5">
                        <a
                          href={c.url || `https://nvd.nist.gov/vuln/detail/${c.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-shinobi-green hover:underline inline-flex items-center gap-1"
                        >
                          {c.id}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td className="px-3 py-1.5 font-mono text-slate-800">{c.cvss_score ?? c.cvss ?? '-'}</td>
                      <td className="px-3 py-1.5">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase border ${SEVERITY_STYLE[(c.severity || 'UNKNOWN').toUpperCase()] || SEVERITY_STYLE.UNKNOWN}`}>
                          {c.severity || 'unknown'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-slate-500">{c.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function CveCell({ entry, isLoading, canEnrich, onEnrich }) {
  if (isLoading) {
    return <span className="text-xs text-slate-500">Looking up...</span>;
  }
  if (!entry) {
    if (!canEnrich) {
      return <span className="text-xs text-slate-500 italic">no version</span>;
    }
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onEnrich(); }}
        className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-700"
      >
        <Sparkles className="w-3 h-3" />
        Enrich
      </button>
    );
  }
  if (entry.count === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider border bg-emerald-100 text-emerald-700 border-emerald-300">
        <CheckCircle2 className="w-3 h-3" />
        No CVEs
      </span>
    );
  }
  // Show severity tally as small chips.
  const tally = entry.cves.reduce((acc, c) => {
    const s = (c.severity || 'UNKNOWN').toUpperCase();
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const order = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'UNKNOWN'];
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-slate-700 tabular-nums">{entry.count}</span>
      {order.map((s) => tally[s] ? (
        <span
          key={s}
          className={`inline-block px-1 py-0 rounded text-[9px] uppercase border ${SEVERITY_STYLE[s]}`}
        >
          {s.charAt(0)}{tally[s]}
        </span>
      ) : null)}
    </div>
  );
}
